import { usePostApply } from '@components/hooks/PostApplyProvider/index.client'
import Button, { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import Link from '@components/primitives/Link'
import Modal from '@components/primitives/Modal/index.client'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe'
import { faMicrophone } from '@fortawesome/pro-regular-svg-icons/faMicrophone'
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark'
import { faHandPointUp } from '@fortawesome/pro-solid-svg-icons/faHandPointUp'
import Routes from '@lib/routes'
import PatientRoutes from '@lib/routes/PatientRoutes'
import { trackEvent } from '@lib/tracking'
import { isInAppBrowser, isIos } from '@lib/tracking/isInAppBrowser'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { PatientNavigatorSkipModal } from './PatientNavigatorSkipModal'

export function VerificationAgentIntro({
  idToken,
  microphoneAccessGranted,
  onStartCall,
  setMicrophoneAccessGranted,
  urlToExitInAppBrowser,
  userAgent,
}: {
  idToken: string
  microphoneAccessGranted: boolean
  onStartCall: () => void
  setMicrophoneAccessGranted: (granted: boolean) => void
  urlToExitInAppBrowser: string
  userAgent: string
}) {
  const [inAppBrowserModalOpen, setInAppBrowserModalOpen] = useState(false)
  const [micModalOpen, setMicModalOpen] = useState(false)
  const [skipModalOpen, setSkipModalOpen] = useState(false)
  const { advanceToNextStep } = usePostApply()
  const router = useRouter()

  const requestMicrophoneAccess = async () => {
    try {
      if (isInAppBrowser()) {
        setInAppBrowserModalOpen(true)
        setMicModalOpen(false)
      } else {
        trackEvent(
          TrackingEvents.PATIENT_NAVIGATOR_AGENT_MICROPHONE_REQUESTED,
          {
            trialApplicationIdToken: idToken,
          },
        )
        setMicModalOpen(true)
        await navigator.mediaDevices.getUserMedia({
          audio: true,
        })
        trackEvent(TrackingEvents.PATIENT_NAVIGATOR_AGENT_MICROPHONE_GRANTED, {
          trialApplicationIdToken: idToken,
        })
        setMicrophoneAccessGranted(true)
        setMicModalOpen(false)
        onStartCall()
      }
    } catch (error: any) {
      // Error types: NotAllowedError, NotFoundError, NotReadableError, etc
      trackEvent(TrackingEvents.PATIENT_NAVIGATOR_AGENT_MICROPHONE_DENIED, {
        trialApplicationIdToken: idToken,
        ...(error.name ? { val: error.name } : {}), // Send error type to segment
      })
      router.push(
        PatientRoutes.trialApplySurveyPostscreenerConsents(
          idToken,
          'why-apply',
        ),
      )
    }
  }

  const exitInAppBrowser = () => {
    // This does not mean the user exited because the in-app browser would have its own modal to confirm after ours
    trackEvent(TrackingEvents.PATIENT_NAVIGATOR_AGENT_IN_APP_BROWSER_EXITED, {
      trialApplicationIdToken: idToken,
    })
    window.open(urlToExitInAppBrowser, '_blank', 'noopener noreferrer')
  }

  return (
    <div className='flex h-full flex-col justify-between'>
      <MicrophonePermissionModal
        isOpen={micModalOpen}
        onClose={setMicModalOpen}
      />
      <ChangeBrowserModal
        isOpen={inAppBrowserModalOpen}
        onClick={exitInAppBrowser}
        onClose={setInAppBrowserModalOpen}
        userAgent={userAgent}
      />
      <PatientNavigatorSkipModal
        isOpen={skipModalOpen}
        onSkip={() => {
          trackEvent(TrackingEvents.PATIENT_NAVIGATOR_AGENT_CALL_SKIPPED)
          advanceToNextStep()
          router.push(
            PatientRoutes.trialApplySurveyPostscreenerConsents(
              idToken,
              'why-apply',
            ),
          )
        }}
        setIsOpen={setSkipModalOpen}
      />
      <button
        className='absolute right-4 top-4 z-navbarContainer flex size-8 items-center justify-center rounded-full bg-neutral200 text-neutral600 hover:bg-neutral300 hover:text-neutral800'
        onClick={() => {
          trackEvent(
            TrackingEvents.PATIENT_NAVIGATOR_AGENT_SKIP_BUTTON_CLICKED,
            {
              trialApplicationIdToken: idToken,
              type: 'x button',
            },
          )
          setSkipModalOpen(true)
        }}
        type='button'
      >
        <Icon className='size-4' icon={faXmark} />
      </button>
      <div className='fixed inset-0 -z-10 bg-gradient-to-b from-white via-white to-primary50' />
      <div className='mx-auto grid max-w-4xl grid-cols-1 items-center gap-x-4 gap-y-8'>
        <Icon
          className='mx-auto size-8 rounded-full bg-primary50 p-8 text-primary500 md:size-16'
          icon={faMicrophone}
          size='2x'
        />
        <Text className={`text-xl font-bold md:text-3xl`} styleName={'h3'}>
          {microphoneAccessGranted
            ? 'Confirm details to a few of your responses'
            : 'Enable your mic to confirm details'}
        </Text>
        <Text className='text-neutral600'>
          Speak with our live agent to verify your responses. Enabling your mic
          is critical to a great call
        </Text>
        <ul className='list-inside list-disc text-neutral600'>
          <li className='text-lg'>Takes 3 minutes tops</li>
          <li className='text-lg'>Please make sure your mic is enabled</li>
          <li className='text-lg'>Your chat may be recorded.</li>
          <li className='text-lg'>
            For more info see our{' '}
            <Link
              className='underline'
              href={Routes.privacyPolicy}
              newTab={true}
              onClick={() => {
                trackEvent(
                  TrackingEvents.PATIENT_NAVIGATOR_AGENT_PRIVACY_POLICY_CLICKED,
                  {
                    trialApplicationIdToken: idToken,
                  },
                )
              }}
            >
              Privacy Notice
            </Link>
          </li>
        </ul>
      </div>
      <div className='fixed inset-x-0 bottom-0 mx-auto flex max-w-4xl flex-row justify-center gap-4 p-4'>
        <Button
          className={classMerge(
            buttonClassNames.legacy.fill.black,
            'pointer-events-auto w-full rounded-full md:w-1/2',
          )}
          onClick={
            microphoneAccessGranted ? onStartCall : requestMicrophoneAccess
          }
        >
          {microphoneAccessGranted ? 'Start Call' : 'Enable Mic'}
          <Icon className={'ml-2'} icon={faMicrophone} />
        </Button>
      </div>
    </div>
  )
}

const MicrophonePermissionModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: (flag: boolean) => void
}) => {
  return (
    <Modal
      className='my-[60%] md:my-[30%] lg:my-[20%]'
      onClose={() => onClose(false)}
      visible={isOpen}
    >
      <div
        className='flex cursor-pointer flex-col items-start gap-8'
        onClick={() => onClose(false)}
      >
        <Icon
          className='size-16 text-primary500'
          icon={faHandPointUp}
          size='2x'
        />
        <div>
          <Text className={`text-xl font-bold md:text-3xl`} styleName={'h3'}>
            Click Allow
          </Text>
          <Text className='text-neutral600'>
            Click allow on the browser pop-up to ensure your microphone is
            enabled for a live call.
          </Text>
        </div>
      </div>
    </Modal>
  )
}

const ChangeBrowserModal = ({
  isOpen,
  onClick,
  onClose,
  userAgent,
}: {
  isOpen: boolean
  onClick: () => void
  onClose: (flag: boolean) => void
  userAgent: string
}) => {
  const browserName = isIos(userAgent) ? 'Safari' : 'Chrome'
  return (
    <Modal
      bodyClassName='pb-6'
      className='my-[40%] md:my-[30%] lg:my-[20%]'
      onClose={() => onClose(false)}
      visible={isOpen}
    >
      <div className='mx-4 flex cursor-pointer flex-col items-start gap-6'>
        <Icon className='size-16 text-primary500' icon={faGlobe} size='2x' />
        <div className='flex flex-col gap-2'>
          <Text className={`text-2xl font-bold md:text-3xl`} styleName={'h3'}>
            Switch to {browserName} to complete this step
          </Text>
          <Text className='text-neutral600'>
            You’re currently in Facebook’s web browser. To finish your
            application, we need to switch to {browserName}.
          </Text>
        </div>
        <div className='w-full'>
          <Button
            className={classMerge(
              buttonClassNames.legacy.fill.black,
              'w-full rounded-lg',
            )}
            onClick={onClick}
          >
            Open in {browserName}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
